import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { ChallengeEventType } from "../../directus/collections";
import { ChallengeMatchTableRow } from "./ChallengeMatchTableRow";

import { webWorkerQuery } from "../../libs/workers/webWorker";
import { EVENTS_LABELS } from "../../shared";
import "./ChallengeMatchOverviewTable.css";
import ChallengeMatchOverviewTableWorker from "./ChallengeMatchOverviewTable.worker.ts?worker";

import { WorkerQueryTransferModes } from "../../libs/workers/consts";
import {
  createMatchEnigmaKey,
  MatchEnigmaDetailedSummary,
  MatchEnigmaIds,
} from "../../services/enigmaStats.service";
import {
  ChallengeMatchOverviewTableWorkerInputType,
  MatchRowData,
  PaginationData,
} from "./ChallengeMatchOverviewTable.types";

// Componente per i dettagli del match espanso

export function ChallengeMatchOverviewTable({
  challengeId,
  columns,
  highlightColumns,
  data,
  totalCount,
  sx: { maxHeight = undefined } = {
    maxHeight: undefined,
  },
}: {
  challengeId: string;
  columns: ChallengeEventType[];
  highlightColumns?: ChallengeEventType[];
  data: MatchEnigmaIds[];
  totalCount: number;
  sx?: {
    maxHeight?: string | number;
  };
}) {
  const [ready, setReady] = React.useState(false);
  const [rows, setRows] = React.useState<MatchRowData[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const workerRef = React.useRef<Worker | null>(null);

  const theme = useTheme();

  console.debug("ChallengeMatchOverviewTable render", { data });

  React.useEffect(() => {
    workerRef.current = new ChallengeMatchOverviewTableWorker();
    challengeMatchOverviewTableWorkerQuery<PaginationData>(
      workerRef.current,
      "INITIALIZE",
      data
    )
      .then(() => {
        console.log("Worker ready");
        setReady(true);
      })
      .catch((error) => {
        console.error("Worker initialization error:", error);
      });
    return () => {
      workerRef.current?.terminate();
    };
  }, []);

  // Caricamento dati paginati
  const updateRows = async (pageNum: number, size: number) => {
    console.log("Updating rows", { pageNum, size });
    setLoading(true);
    if (!workerRef.current) {
      console.error("Worker non inizializzato");
      setLoading(false);
      return;
    }

    try {
      const data = await challengeMatchOverviewTableWorkerQuery<
        MatchEnigmaDetailedSummary[]
      >(workerRef.current, "GET_PAGE", {
        page: pageNum,
        pageSize: size,
        challengeId,
      });

      setRows(
        data?.map((row) => ({
          id: createMatchEnigmaKey({
            matchId: row.matchId,
            targetId: row.targetId,
          }),
          challengeId: row.targetId,
          matchId: row.matchId,
          teamName: row.teamName,
          gameLink: row.gameLink,
          challengeName: row.challengeName,
          startDate: row.startedAt,
          expanded: false,
          events: row.events,
        })) ?? []
      );
    } catch (error) {
      console.error("Errore caricamento dati:", error);
    } finally {
      setLoading(false);
    }
  };

  // Caricamento iniziale
  React.useEffect(() => {
    if (ready) {
      updateRows(page, pageSize);
    }
  }, [ready, page, pageSize]);

  // Gestione cambio pagina
  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(event.target.value, 10);
    setPageSize(newSize);
    setPage(0); // Reset alla prima pagina
  };

  return (
    <Box className="challenge-match-overview-table" sx={{ width: "100%" }}>
      <TableContainer component={Paper} sx={{ maxHeight: maxHeight }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.background.paper }}>
              <TableCell width={30}></TableCell>
              <TableCell>Match ID</TableCell>
              <TableCell>Enigma</TableCell>
              <TableCell>Data Inizio</TableCell>
              {columns.map((column) => (
                <TableCell
                  key={column}
                  sx={{
                    backgroundColor: highlightColumns?.includes(column)
                      ? theme.palette.primary.dark
                      : "inherit",
                    fontWeight: highlightColumns?.includes(column)
                      ? "bold"
                      : "normal",
                    color: highlightColumns?.includes(column)
                      ? theme.palette.primary.contrastText
                      : "inherit",
                  }}
                >
                  {EVENTS_LABELS[column] || column}
                </TableCell>
              ))}
              <TableCell width={100}>Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 6}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <CircularProgress />
                  <Typography sx={{ mt: 1 }}>Caricamento dati...</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <ChallengeMatchTableRow
                  key={row.id}
                  row={row}
                  columns={columns}
                  highlightColumns={highlightColumns}
                  onToggleExpand={async (
                    matchId: string,
                    challengeId: string
                  ) => {
                    // Sposta qui la logica di toggle/espansione e caricamento dettagli
                    setRows((prevRows) =>
                      prevRows.map((row) =>
                        row.matchId === matchId &&
                        row.challengeId === challengeId
                          ? { ...row, expanded: !row.expanded }
                          : row
                      )
                    );
                  }}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginazione custom */}
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={pageSize}
        onRowsPerPageChange={handlePageSizeChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Righe per pagina:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`
        }
      />
    </Box>
  );
}

export async function challengeMatchOverviewTableWorkerQuery<T>(
  worker: Worker,
  type: ChallengeMatchOverviewTableWorkerInputType,
  data?: any
): Promise<T | null> {
  return webWorkerQuery<string, T>(
    worker,
    {
      type,
      data,
      opts: {
        response: {
          transferMode: WorkerQueryTransferModes.SHARED_ARRAY_BUFFER,
        },
      },
    }
    // { transferMode: WorkerQueryTransferModes.CHUNKED }
  );
}
